import { z } from 'zod';

export const invoiceIdentityFieldSchema = z.object({
  value: z.string(),
  source: z.string(),
});

const identityFieldsSchema = z.object({
  legal_name: invoiceIdentityFieldSchema,
  dba: invoiceIdentityFieldSchema,
  billing_address: invoiceIdentityFieldSchema,
});

const identityProjectionSchema = z.object({
  screen: identityFieldsSchema,
  pdf: identityFieldsSchema,
  client_display_name: invoiceIdentityFieldSchema,
}).passthrough();

export const invoiceChangeIntentSchema = z.discriminatedUnion('intent', [
  z.object({
    intent: z.literal('shared_correction'),
    organization_id: z.string(),
    establishment_id: z.number().int().optional(),
    fields: z.partialRecord(z.enum(['legal_name', 'display_name', 'dba', 'billing_address', 'address']), z.string())
      .refine((fields) => Object.keys(fields).length > 0, 'At least one identity field is required'),
  }),
  z.object({
    intent: z.literal('project_address_correction'),
    project_ids: z.array(z.string()),
    fields: z.object({ billing_address: z.string() }),
    future_defaults: z.literal(true),
    project_address_field: z.enum(['site_address', 'billing_address']).optional(),
  }),
  z.object({
    intent: z.literal('identity_default'),
    organization_id: z.string(),
    future_defaults: z.literal(true),
    default_level: z.enum(['this_project', 'this_client']),
    default_field: z.enum(['dba', 'billing_address', 'hide_dba']),
    focused_invoice_id: z.string().optional(),
    project_ids: z.array(z.string()).optional(),
    client_scope_ids: z.array(z.string()).optional(),
    default_establishment_id: z.number().int().nullable().optional(),
    default_billing_address_source: z.enum(['organization', 'project', 'custom', 'establishment']).optional(),
    default_billing_address_override: z.string().nullable().optional(),
    default_hide_dba: z.boolean().optional(),
  }),
  z.object({
    intent: z.literal('project_assignment'),
    organization_id: z.string(),
    target_organization_id: z.string(),
    project_ids: z.array(z.string()),
    client_scope_ids: z.array(z.string()),
    target_establishment_id: z.number().int().nullable().optional(),
    future_defaults: z.boolean().optional(),
    // R2 AC9: {source client_scope_id: target client_scope_id} when the target client already has several billing
    // tracks on a project and she picked which one to use.
    target_client_scope_choices: z.record(z.string(), z.string()).optional(),
  }),
  z.object({
    intent: z.literal('invoice_assignment'),
    invoice_id: z.string(),
    organization_id: z.string(),
    target_organization_id: z.string(),
    target_client_scope_id: z.string().nullable().optional(),
    target_establishment_id: z.number().int().nullable().optional(),
  }),
  z.object({
    intent: z.literal('invoice_override'),
    invoice_id: z.string(),
    fields: z.partialRecord(z.enum(['legal_name', 'display_name', 'dba', 'billing_address']), z.string())
      .refine((fields) => Object.keys(fields).length > 0, 'At least one identity field is required'),
  }),
  z.object({
    intent: z.literal('invoice_options'),
    invoice_id: z.string(),
    invoice_billing_address_source: z.enum(['organization', 'project', 'establishment']).optional(),
    invoice_hide_dba: z.boolean().optional(),
  }),
  z.object({
    intent: z.literal('follow_default'),
    invoice_id: z.string(),
    follow_fields: z.array(z.enum(['legal_name', 'display_name', 'dba', 'billing_address'])),
  }),
]);

export type InvoiceChangeIntent = z.infer<typeof invoiceChangeIntentSchema>;

// Pydantic serializes every defaulted/optional common-model field in the plan's
// echoed intent. This response parser retains that complete echo while the
// request type above stays a precise discriminated union.
const invoiceChangeIntentEchoSchema = z.object({
  intent: z.enum([
    'shared_correction', 'project_address_correction', 'identity_default',
    'project_assignment', 'invoice_assignment', 'invoice_override', 'invoice_options', 'follow_default',
  ]),
  organization_id: z.string().nullable().optional(),
  establishment_id: z.number().int().nullable().optional(),
  target_organization_id: z.string().nullable().optional(),
  target_establishment_id: z.number().int().nullable().optional(),
  target_client_scope_id: z.string().nullable().optional(),
  target_client_scope_choices: z.record(z.string(), z.string()).optional(),
  project_ids: z.array(z.string()).optional(),
  client_scope_ids: z.array(z.string()).optional(),
  invoice_id: z.string().nullable().optional(),
  focused_invoice_id: z.string().nullable().optional(),
  fields: z.record(z.string(), z.unknown()).optional(),
  follow_fields: z.array(z.string()).optional(),
  future_defaults: z.boolean().optional(),
  default_level: z.enum(['this_project', 'this_client']).nullable().optional(),
  default_field: z.enum(['dba', 'billing_address', 'hide_dba']).nullable().optional(),
  default_establishment_id: z.number().int().nullable().optional(),
  default_billing_address_source: z.string().nullable().optional(),
  default_billing_address_override: z.string().nullable().optional(),
  default_hide_dba: z.boolean().nullable().optional(),
  invoice_billing_address_source: z.enum(['organization', 'project', 'establishment']).nullable().optional(),
  invoice_hide_dba: z.boolean().nullable().optional(),
  project_address_field: z.enum(['site_address', 'billing_address']).nullable().optional(),
}).passthrough();

// `by` (R2 AC7) is who recorded the send, as stored (an email address); `by_name` (R3 AC5/AC9) is the name she reads.
// R3 AC9: ONE send rule on the server. `marked_sent` is her own Mark as Sent that left only `email_sent_at`, or a status
// of sent/overdue; `at` is null when no date was ever recorded (`date_recorded` false).
const issuanceProvenanceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unknown'), at: z.null() }),
  z.object({ kind: z.literal('provider_accepted'), at: z.string(), by: z.string().nullable().optional(),
    by_name: z.string().nullable().optional() }),
  z.object({ kind: z.literal('manual_internal_send_resolution'), at: z.string(), by: z.string().nullable().optional(),
    by_name: z.string().nullable().optional() }),
  z.object({
    kind: z.literal('manual_external_send_assertion'),
    at: z.string(),
    by: z.string().nullable().optional(),
    by_name: z.string().nullable().optional(),
    document_revision_id: z.string().nullable(),
  }),
  z.object({
    kind: z.literal('marked_sent'),
    at: z.string().nullable(),
    by: z.string().nullable().optional(),
    by_name: z.string().nullable().optional(),
    date_recorded: z.boolean().optional(),
  }),
]);

const savedRecipientSchema = z.object({
  invoice_contact_id: z.string(),
  contact_id: z.string().nullable(),
  name: z.string(),
  email: z.string().nullable(),
  role: z.string().nullable(),
});

const recipientEffectsSchema = z.object({
  retained_typed_in_ids: z.array(z.string()),
  retained_target_contact_ids: z.array(z.string()),
  removed_other_linked_ids: z.array(z.string()),
  retained_typed_in: z.array(savedRecipientSchema),
  retained_target_contacts: z.array(savedRecipientSchema),
  removed_other_linked: z.array(savedRecipientSchema),
});

const invoiceChangeCandidateSchema = z.object({
  invoice_id: z.string(),
  invoice_number: z.string(),
  project_id: z.string(),
  project_name: z.string().nullable(),
  project_is_archived: z.boolean(),
  status: z.string(),
  before: identityProjectionSchema,
  after: identityProjectionSchema,
  issuance_provenance: issuanceProvenanceSchema,
  requires_post_send_acknowledgement: z.boolean(),
  preservation_required_if_excluded: z.literal(true),
  /** R5 AC5: false for a cancelled invoice (listed, but only changed when she ticks it). Older servers omit it. */
  default_selected: z.boolean().optional(),
  recipient_effects: recipientEffectsSchema.nullable().optional(),
}).passthrough();

const sourceActionSchema = z.object({
  invoice_id: z.string(),
  invoice_number: z.string(),
  project_id: z.string(),
  project_name: z.string().nullable(),
  status: z.string(),
  before: identityProjectionSchema,
  after: identityProjectionSchema,
  fields: z.array(z.string()),
  rendered_value_changes: z.boolean(),
  issuance_provenance: issuanceProvenanceSchema,
  requires_post_send_acknowledgement: z.boolean(),
  recipient_effects: recipientEffectsSchema.nullable().optional(),
}).passthrough();

const invoiceChangeSourceChangeSchema = z.object({
  source_type: z.string(),
  source_id: z.string(),
  project_name: z.string().nullable().optional(),
  field: z.string(),
  before: z.unknown(),
  after: z.unknown(),
}).passthrough();

const selectedProjectDefaultSchema = z.object({
  project_id: z.string().optional(),
  project_name: z.string().nullable().optional(),
  project_address: z.string().nullable().optional(),
  client_scope_id: z.string().optional(),
  organization_id: z.string().optional(),
  organization_name: z.string().nullable().optional(),
  field: z.string().optional(),
  before: z.record(z.string(), z.unknown()).optional(),
  after: z.record(z.string(), z.unknown()).optional(),
  before_organization_id: z.string().optional(),
  after_organization_id: z.string().optional(),
  before_organization_name: z.string().nullable().optional(),
  after_organization_name: z.string().nullable().optional(),
  scope_type_id: z.string().optional(),
  project_number: z.string().nullable().optional(),
  before_is_primary: z.boolean().optional(),
  prior_primary_client_scope_ids: z.array(z.string()).optional(),
  before_establishment_id: z.number().int().nullable().optional(),
  after_establishment_id: z.number().int().nullable().optional(),
  before_establishment_name: z.string().nullable().optional(),
  after_establishment_name: z.string().nullable().optional(),
  source_field: z.enum(['site_address', 'billing_address']).optional(),
  before_billing_address: z.string().nullable().optional(),
  after_billing_address: z.string().nullable().optional(),
}).passthrough();

/** R2 AC9: per moved project, whether the target client's billing track is reused, created, or needs a choice. */
const targetTrackSchema = z.object({
  project_id: z.string(),
  project_name: z.string().nullable().optional(),
  project_number: z.string().nullable().optional(),
  target_organization_name: z.string().nullable().optional(),
  source_client_scope_id: z.string().nullable(),
  action: z.enum(['reuse', 'create', 'choose']),
  target_client_scope_id: z.string().nullable(),
  target_scope_type_id: z.string().nullable(),
  target_scope_type_name: z.string().nullable(),
  candidates: z.array(z.object({
    id: z.string(),
    scope_type_id: z.string(),
    scope_type_name: z.string().nullable().optional(),
    is_primary: z.boolean().optional(),
  })),
}).passthrough();

/** R2 AC10: the people saved for a moved project (contacts of the old client), for the keep or drop choice. */
const projectRecipientsSchema = z.object({
  project_id: z.string(),
  project_name: z.string().nullable().optional(),
  project_number: z.string().nullable().optional(),
  client_scope_id: z.string(),
  people: z.array(z.object({
    contact_id: z.string(),
    name: z.string(),
    email: z.string(),
    role: z.string(),
  })),
}).passthrough();

export const invoiceChangePlanSchema = z.object({
  plan_id: z.string().length(64),
  intent: invoiceChangeIntentEchoSchema,
  candidates: z.array(invoiceChangeCandidateSchema),
  candidate_count: z.number().int().nonnegative(),
  source_actions: z.array(sourceActionSchema),
  source_changes: z.array(invoiceChangeSourceChangeSchema).optional(),
  // R5 iteration 5 (CHR-R5-22): invoices combined into another are not candidates; the review says so.
  combined_away_invoices: z.array(z.object({
    invoice_id: z.string(),
    invoice_number: z.string().nullable().optional(),
    merged_into_invoice_id: z.string(),
    merged_into_invoice_number: z.string().nullable().optional(),
  }).passthrough()).optional(),
  target_tracks: z.array(targetTrackSchema).optional(),
  project_recipients: z.array(projectRecipientsSchema).optional(),
  // R2 AC8: for a shared correction, the client and how many projects use it.
  shared_source: z.object({
    organization_id: z.string(),
    organization_name: z.string().nullable().optional(),
    project_count: z.number().int().nonnegative(),
  }).nullable().optional(),
  unresolved_unscoped_invoice_ids: z.array(z.string()),
  unresolved_unscoped_invoices: z.array(z.object({
    invoice_id: z.string(),
    invoice_number: z.string().nullable(),
    project_id: z.string(),
    reason: z.string(),
    next_action: z.string(),
  }).passthrough()),
  unchanged_count: z.number().int().nonnegative(),
  selected_project_defaults: z.array(selectedProjectDefaultSchema),
  preserved_excluded_candidates: z.array(z.string()),
  selection_contract: z.string(),
}).passthrough();

export type InvoiceChangePlan = z.infer<typeof invoiceChangePlanSchema>;

const uniqueStrings = (values: string[]) => new Set(values).size === values.length;

export const invoiceChangeApplyRequestSchema = z.object({
  plan_id: z.string().length(64),
  intent: invoiceChangeIntentSchema,
  selected_invoice_ids: z.array(z.string()).default([]),
  selected_project_default_scope_ids: z.array(z.string()).default([]),
  selected_project_ids: z.array(z.string()).default([]),
  apply_source_update: z.boolean().default(false),
  acknowledge_sent_invoice_ids: z.array(z.string()).default([]),
  acknowledge_recipient_removal_invoice_ids: z.array(z.string()).default([]),
  // R2 AC10: people saved for the moved project that she ticked "Keep on {project} invoices". None ticked drops all.
  keep_project_recipient_contact_ids: z.array(z.string()).default([]),
  operation_key: z.string().min(1).max(128),
}).superRefine((request, context) => {
  const groups = [request.selected_invoice_ids, request.selected_project_default_scope_ids,
    request.selected_project_ids, request.acknowledge_sent_invoice_ids,
    request.acknowledge_recipient_removal_invoice_ids, request.keep_project_recipient_contact_ids];
  if (groups.some((ids) => !uniqueStrings(ids))) {
    context.addIssue({ code: 'custom', message: 'Selection IDs must be unique' });
  }
  const { intent } = request;
  if (!request.selected_invoice_ids.length && !request.selected_project_default_scope_ids.length
      && !request.selected_project_ids.length && !request.apply_source_update) {
    context.addIssue({ code: 'custom', message: 'Choose an invoice or future project default' });
  }
  if (intent.intent === 'shared_correction' && !request.apply_source_update) {
    context.addIssue({ code: 'custom', message: 'Explicitly approve the shared source update' });
  }
  if (request.apply_source_update && intent.intent !== 'shared_correction' && intent.intent !== 'identity_default') {
    context.addIssue({ code: 'custom', message: 'Source update is only valid for a shared correction' });
  }
  if (intent.intent === 'identity_default') {
    if (intent.default_level === 'this_client' && !request.apply_source_update) {
      context.addIssue({ code: 'custom', message: 'Explicitly approve the client default update' });
    }
    if (intent.default_level === 'this_project' && !request.selected_project_default_scope_ids.length
        && !(intent.focused_invoice_id && request.selected_invoice_ids.length)) {
      context.addIssue({ code: 'custom', message: 'Choose a reviewed project default scope' });
    }
    if (intent.default_level === 'this_project' && request.apply_source_update) {
      context.addIssue({ code: 'custom', message: 'Project defaults use selected_project_default_scope_ids' });
    }
  }
  if (request.selected_project_default_scope_ids.length
      && !('future_defaults' in intent && intent.future_defaults)) {
    context.addIssue({ code: 'custom', message: 'Future defaults were not reviewed' });
  }
  if (request.selected_project_ids.length && intent.intent !== 'project_address_correction') {
    context.addIssue({ code: 'custom', message: 'Project address defaults were not reviewed' });
  }
});

export type InvoiceChangeApplyRequest = z.infer<typeof invoiceChangeApplyRequestSchema>;

export const invoiceChangeApplyResultSchema = z.object({
  operation_key: z.string(),
  plan_id: z.string(),
  changed_invoice_ids: z.array(z.string()),
  selected_invoice_ids: z.array(z.string()),
  preserved_invoice_ids: z.array(z.string()),
  materialized_preservation_invoice_ids: z.array(z.string()),
  selected_project_default_scope_ids: z.array(z.string()),
  selected_project_ids: z.array(z.string()),
}).passthrough();

export type InvoiceChangeApplyResult = z.infer<typeof invoiceChangeApplyResultSchema>;

const materialFieldSchema = z.object({ before: z.unknown(), after: z.unknown() }).passthrough();
const materialFieldsSchema = z.object({
  legal_name: materialFieldSchema.optional(),
  display_name: materialFieldSchema.optional(),
  dba: materialFieldSchema.optional(),
  billing_address: materialFieldSchema.optional(),
}).passthrough();

const billingChangeSchema = z.object({
  id: z.string(),
  operation_id: z.string().nullable(),
  actor: z.string(),
  intent: z.string(),
  source_records: z.unknown(),
  before_fields: z.record(z.string(), z.unknown()),
  after_fields: z.record(z.string(), z.unknown()),
  changed_fields: z.array(z.string()),
  selected: z.boolean(),
  created_at: z.string(),
  material_fields: materialFieldsSchema.optional(),
  // R3 typed dispatch: which kind of recorded operation this is. A reader renders the kinds
  // it understands; an unknown value is shown generically, never interpreted.
  event_type: z.string().optional(),
}).passthrough();

// ---- SPEC-invoice-merge-history R3 ------------------------------------------------------
export const lineageMemberSchema = z.object({
  invoice_id: z.string(),
  invoice_number: z.string().nullable(),
  status: z.string().nullable(),
  role: z.enum(['current', 'ancestor']),
  is_requested: z.boolean(),
  merged_into_invoice_id: z.string().nullable(),
  merged_into_invoice_number: z.string().nullable(),
  merged_at: z.string().nullable(),
  merged_by: z.string().nullable(),
  merge_origin: z.string().nullable(),
  merge_operation_id: z.string().nullable().optional(),
  provenance_note: z.string().nullable().optional(),
}).passthrough();

export const invoiceLineageSchema = z.object({
  requested_invoice_id: z.string(),
  current_invoice_id: z.string(),
  current_invoice_number: z.string().nullable(),
  current_invoice_status: z.string().nullable().optional(),
  is_merged_away: z.boolean(),
  has_merge_history: z.boolean(),
  members: z.array(lineageMemberSchema),
}).passthrough();

export const currentVersionSchema = z.object({
  state: z.string(),
  reason_code: z.string().optional(),
  reason: z.string().optional(),
  invoice_id: z.string().optional(),
  last_recorded_send_at: z.string().nullable().optional(),
  latest_change_at: z.string().nullable().optional(),
  historical_send_count: z.number().int().nonnegative().optional(),
  has_merge_history: z.boolean().optional(),
  // R5 iteration 4: how the latest send is evidenced. 'archived_copy' = tied to a stored PDF; 'operator_assertion' = a
  // person recorded a send naming no saved copy (attachment bytes not proven); 'legacy_total' = only an old total.
  send_evidence: z.string().nullable().optional(),
  note: z.string().optional(),
}).passthrough();

const unsupportedEventSchema = z.object({
  id: z.string(),
  intent: z.string(),
  actor: z.string().nullable(),
  created_at: z.string(),
  operation_id: z.string().nullable().optional(),
}).passthrough();

/** A shared line-description edit (intent billable_item_description) with the old and new text it recorded. */
const descriptionChangeSchema = z.object({
  id: z.string(),
  actor: z.string().nullable(),
  created_at: z.string(),
  operation_id: z.string().nullable().optional(),
  invoice_number: z.string().nullable().optional(),
  billable_item_id: z.string().nullable().optional(),
  before: z.string().nullable().optional(),
  after: z.string().nullable().optional(),
}).passthrough();

/**
 * R5 iteration 2 (CHR-R5-01): in a merged lineage EVERY recorded row says whether it belongs to an original
 * invoice (before combining) or to the combined/current version. Absent on an unmerged invoice and on older servers.
 */
export const mergeVersionFields = {
  version_role: z.string().optional(),
  before_combining: z.boolean().optional(),
  version_label: z.string().optional(),
  original_invoice_number: z.string().nullable().optional(),
  invoice_total: z.string().nullable().optional(),
  combined_with_numbers: z.array(z.string()).optional(),
  merged_into_invoice_number: z.string().nullable().optional(),
  // R5 iteration 3 (CHR-R5-10/-11): each saved copy is labelled from ITS OWN facts. `invoice_total` is the copy's own
  // recorded total (`total_source` says where it came from; null = none recorded, never a merge-time guess).
  version_tag: z.string().optional(),
  version_note: z.string().nullable().optional(),
  is_current_version: z.boolean().optional(),
  total_source: z.string().nullable().optional(),
  later_merge_text: z.string().nullable().optional(),
  download_suffix: z.string().optional(),
  copy_revision_id: z.string().optional(),
};

export const lineageHistoryEventSchema = z.object({
  ...mergeVersionFields,
  event_id: z.string(),
  source: z.string(),
  source_row_id: z.string(),
  kind: z.string(),
  occurred_at: z.string(),
  invoice_id: z.string().nullable(),
  invoice_number: z.string().nullable(),
  current_invoice_id: z.string(),
  is_original_invoice: z.boolean(),
  actor: z.string().nullable(),
  provenance: z.object({
    source_table: z.string(),
    origin: z.string(),
    label: z.string(),
  }).passthrough(),
  details: z.record(z.string(), z.unknown()),
  event_type: z.string().optional(),
  evidence_class: z.string().optional(),
  delivery_claim: z.string().optional(),
}).passthrough();

export const invoiceLineageHistoryPageSchema = z.object({
  requested_invoice_id: z.string(),
  current_invoice_id: z.string(),
  lineage: invoiceLineageSchema,
  events: z.array(lineageHistoryEventSchema),
  limit: z.number().int(),
  has_more: z.boolean(),
  next_cursor: z.string().nullable(),
}).passthrough();

export type InvoiceLineage = z.infer<typeof invoiceLineageSchema>;
export type LineageHistoryEvent = z.infer<typeof lineageHistoryEventSchema>;
export type InvoiceLineageHistoryPage = z.infer<typeof invoiceLineageHistoryPageSchema>;

export const invoiceDocumentRevisionSchema = z.object({
  id: z.string(),
  content_sha256: z.string(),
  byte_size: z.number().int().nonnegative(),
  filename: z.string(),
  created_by: z.string(),
  created_at: z.string(),
  // R3: the ORIGINAL invoice this saved copy belongs to (it may sit on an invoice that was
  // later merged away) and where the copy came from.
  invoice_id: z.string().optional(),
  invoice_number: z.string().nullable().optional(),
  is_original_invoice: z.boolean().optional(),
  evidence_origin: z.string().optional(),
  ...mergeVersionFields,
}).passthrough();

export const invoiceDocumentEventSchema = z.object({
  id: z.string(),
  revision_id: z.string().nullable(),
  send_attempt_id: z.string().nullable(),
  kind: z.string(),
  actor: z.string(),
  details: z.record(z.string(), z.unknown()).nullable(),
  occurred_at: z.string(),
  invoice_id: z.string().optional(),
  invoice_number: z.string().nullable().optional(),
  is_original_invoice: z.boolean().optional(),
  evidence_class: z.string().optional(),
  delivery_claim: z.string().optional(),
  ...mergeVersionFields,
}).passthrough();

export const invoiceSendAttemptHistorySchema = z.object({
  id: z.string(),
  state: z.string(),
  document_revision_id: z.string().nullable(),
  sent_by: z.string(),
  created_at: z.string(),
  dispatch_started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  resolved_by: z.string().nullable(),
  last_error: z.string().nullable(),
  document_evidence: z.enum(['explicit_revision', 'unknown']),
  invoice_id: z.string().optional(),
  invoice_number: z.string().nullable().optional(),
  is_original_invoice: z.boolean().optional(),
  document_availability: z.string().optional(),
  ...mergeVersionFields,
}).passthrough();

const fieldProvenanceSchema = z.object({
  kind: z.enum(['inherited', 'explicit_override', 'kept_during_edit']),
  source: z.string(),
}).passthrough();

export const invoiceDocumentHistoryDataSchema = z.object({
  invoice_id: z.string(),
  revisions: z.array(invoiceDocumentRevisionSchema),
  events: z.array(invoiceDocumentEventSchema),
  send_attempts: z.array(invoiceSendAttemptHistorySchema),
  billing_changes: z.array(billingChangeSchema),
  first_recorded_send_at: z.string().nullable(),
  billing_details_changed_after_recorded_send: z.boolean(),
  // Added by the R4 provenance contract. Optional while older server responses
  // remain in service; unknown fields survive parsing via passthrough.
  identity_change_events: z.array(billingChangeSchema).optional(),
  current_field_provenance: z.object({
    legal_name: fieldProvenanceSchema,
    display_name: fieldProvenanceSchema,
    dba: fieldProvenanceSchema,
    billing_address: fieldProvenanceSchema,
  }).partial().passthrough().optional(),
  // R3: optional while older servers remain in service.
  lineage: invoiceLineageSchema.optional(),
  current_version: currentVersionSchema.optional(),
  unsupported_events: z.array(unsupportedEventSchema).optional(),
  description_changes: z.array(descriptionChangeSchema).optional(),
}).passthrough();

export type InvoiceDocumentHistoryData = z.infer<typeof invoiceDocumentHistoryDataSchema>;

export type InvoiceIdentityFieldName = 'legal_name' | 'display_name' | 'dba' | 'billing_address';
